import React, { useCallback } from 'react';
import { ClipboardIcon, SourceCodeIcon, Tick02Icon } from 'hugeicons-react';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { TextFileRecord, TextAttachmentViewType } from '../../types/files';
import { CodeMirrorEditor } from '../CodeEditor/CodeMirrorEditor';
import { TipTapEditor } from '../DocEditor/TipTapEditor';
import '../../Styles/Layout/Resources/ResourceDraftEditor.css';

const LANGUAGE_OPTIONS = [
  ['javascript', 'JavaScript'], ['typescript', 'TypeScript'], ['python', 'Python'],
  ['java', 'Java'], ['csharp', 'C#'], ['cpp', 'C++'], ['html', 'HTML'], ['css', 'CSS'],
  ['sql', 'SQL'], ['json', 'JSON'], ['yaml', 'YAML'], ['bash', 'Bash'], ['text', 'Plain text'],
] as const;

const LANGUAGE_EXTENSION: Record<string, string> = {
  javascript: 'js', typescript: 'ts', python: 'py', java: 'java', csharp: 'cs', cpp: 'cpp',
  html: 'html', css: 'css', sql: 'sql', json: 'json', yaml: 'yaml', bash: 'sh', text: 'txt',
};

const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, '\n');
  return (container.textContent || '').replace(/\n{3,}/g, '\n\n').trimEnd();
};

const plainTextToDocumentHtml = (text: string): string => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return text ? `<pre><code>${escaped}</code></pre>` : '';
};

interface ResourceDraftEditorProps {
  file: TextFileRecord;
  onDone: () => void;
}

export const ResourceDraftEditor: React.FC<ResourceDraftEditorProps> = ({ file, onDone }) => {
  const { setTextFiles } = useAttachmentContext();
  const language = file.codeLanguage || 'javascript';

  const updateFile = useCallback((patch: Partial<TextFileRecord>) => {
    setTextFiles(previous => previous.map(item =>
      item.id === file.id
        ? { ...item, ...patch, url: '' }
        : item
    ));
  }, [file.id, setTextFiles]);

  const changeType = useCallback((type: TextAttachmentViewType) => {
    if (type === file.type) return;
    const extension = type === 'code' ? LANGUAGE_EXTENSION[language] || 'js' : 'md';
    const name = /\.[^.]+$/.test(file.name)
      ? file.name.replace(/\.[^.]+$/, `.${extension}`)
      : `${file.name}.${extension}`;
    const convertedContent = type === 'code'
      ? htmlToPlainText(file.content || '')
      : plainTextToDocumentHtml(file.content || '');
    updateFile({
      type,
      name,
      codeLanguage: type === 'code' ? language : undefined,
      content: convertedContent,
      size: new Blob([convertedContent]).size,
    });
  }, [file.content, file.name, file.type, language, updateFile]);

  const changeLanguage = useCallback((nextLanguage: string) => {
    const extension = LANGUAGE_EXTENSION[nextLanguage] || 'txt';
    const name = /\.[^.]+$/.test(file.name)
      ? file.name.replace(/\.[^.]+$/, `.${extension}`)
      : `${file.name}.${extension}`;
    updateFile({ codeLanguage: nextLanguage, name });
  }, [file.name, updateFile]);

  const updateContent = useCallback((content: string) => {
    updateFile({ content, size: new Blob([content]).size });
  }, [updateFile]);

  return (
    <div className="resource-draft-editor">
      <div className="resource-draft-toolbar">
        <div className="resource-draft-kind" role="tablist" aria-label="Resource type">
          <button
            type="button"
            className={file.type === 'doc' ? 'active' : ''}
            onClick={() => changeType('doc')}
          >
            <ClipboardIcon size={15} />
            Document
          </button>
          <button
            type="button"
            className={file.type === 'code' ? 'active' : ''}
            onClick={() => changeType('code')}
          >
            <SourceCodeIcon size={15} />
            Code
          </button>
        </div>
        {file.type === 'code' && (
          <select
            className="resource-draft-language"
            value={language}
            onChange={event => changeLanguage(event.target.value)}
            aria-label="Code language"
          >
            {LANGUAGE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        )}
      </div>

      <div className={`resource-draft-content ${file.type}`}>
        {file.type === 'code' ? (
          <CodeMirrorEditor
            key={file.id}
            value={file.content || ''}
            onChange={updateContent}
            language={language}
            placeholder="Start writing code…"
            className="codemirror-editor resource-code-editor"
          />
        ) : (
          <TipTapEditor
            key={file.id}
            content={file.content || ''}
            onChange={updateContent}
            placeholder="Start writing…"
            className="resource-document-editor"
          />
        )}
      </div>

      <div className="resource-draft-footer">
        <span>
          <Tick02Icon size={14} />
          Saved automatically · sends with your next message
        </span>
        <button type="button" onClick={onDone}>Done</button>
      </div>
    </div>
  );
};
